import { useCallback, useEffect, useState } from 'react';
import { log } from '../../app/logger';
import { recorder } from '../../app/recorder';
import { config } from '../../config';
import { useRecorderView } from '../../ui/capture/useRecorder';
import { Icon } from '../Icon';
import { flagNoticeFor, recordingCaption } from './bandB';
import type { UndoRowB } from './bandB';
import { OverflowSheetB } from './OverflowSheetB';
import { UndoPillB } from './UndoPillB';
import type { MatchRow } from '../../types';

/**
 * B-11 — the takeover chrome's **bottom band** (design spec §2.4): the thumb zone. One fixed
 * box (height set in `capture.css` since B-8 — this unit fills the slot; nothing moves the day
 * it lands), holding:
 *
 * - **The Flag button** — largest target, center (`recorder.pressFlag`: one press files the
 *   flag at the current minute, with audio when the mic is granted, silently without when not
 *   — FLAG-4; a second press stops, because at most one recording exists process-wide and a
 *   press while recording stops it). It renders in **one place, in every state — never
 *   disabled, never hidden** (A's FLAG-4 discipline, kept): pre-kickoff the press is accepted,
 *   the recorder refuses it, and the band says why. While recording, the button IS the brand
 *   **recording chip**: max-contrast border + pulsing dot + the minute the Observation was
 *   stamped with at START (SES-10 — what the user is told is what was written), never a live
 *   clock. The chip keys off `view.recording`, which only a REAL recording populates — a
 *   silent flag creates none, so the band structurally cannot appear to be recording while
 *   silently failing to (FLAG-4's mechanism, not a remembered branch).
 * - **The undo pill** (`UndoPillB`) — directly above the flag, in a **permanently reserved
 *   pill row inside the band's fixed box** (A's UndoStrip discipline: *"the band keeps its
 *   height whether or not it has a target"*). Deliberately NOT an overlay: the first build
 *   floated the pill over the middle band's bottom edge, and a browser probe measured a tap
 *   on the list's tail row blocked for the pill's whole 8-second window — on the locked
 *   board, where bottom tiles can never scroll clear, that overlay would steal increments on
 *   the hottest surface in the app. Reserved chrome costs a strip of middle-band height once,
 *   at build time; an overlay costs the user's taps forever. Only the transient MESSAGES
 *   (notice, hint) overlay — and their container swallows no taps (`pointer-events: none`;
 *   the hint's dismiss alone opts back in).
 * - **Flank slots** — Set piece (B-12) and Flags review (B-13), both live: each button opens
 *   the surface the shell owns, at the 44 pt geometry B-11 reserved, so neither landing ever
 *   re-centered the flag.
 * - **The `⋯` overflow** — guarded Abandon and nothing else destructive (`OverflowSheetB`).
 *   The button itself never appears or disappears (a control that materialises on a predicate
 *   is a reflow on committed chrome — A's §2.3a rule); what the sheet offers varies inside it.
 */

const MODULE = 'ui-b/band';

export interface BottomBandBProps {
  readonly match: MatchRow;
  /** Every hypothesis the shell knows, for `pickUndoTarget` — surfaces publish, the shell merges. */
  readonly undoRows: readonly UndoRowB[];
  /** An undo committed — the shell re-reads (its own rows and the live surface's). */
  readonly onUndone: () => void;
  /** Abandon committed — the host re-reads the row; the hand-off follows from `fullTime`. */
  readonly onTransitioned: () => void;
  /** B-12 — the set-piece flank slot's press: the shell opens the sheet over the middle band. */
  readonly onSetPiece: () => void;
  /** B-13 — the flags-review flank slot's press: the shell shows the review as the middle band. */
  readonly onFlagsReview: () => void;
}

export function BottomBandB({
  match,
  undoRows,
  onUndone,
  onTransitioned,
  onSetPiece,
  onFlagsReview,
}: BottomBandBProps) {
  const view = useRecorderView();
  const [notice, setNotice] = useState<string | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const recording = view.recording;
  const live = recording !== null;

  /**
   * A notice is true in the state that produced it and stale the moment the state moves —
   * screenshot review caught the pre-kickoff refusal ("the match has not kicked off") still
   * rendering, now false, in the first half. Cleared on every transition; within a state a
   * notice persists until the next press replaces it (A's bar behaves the same way).
   */
  useEffect(() => {
    setNotice(null);
  }, [match.state]);

  /**
   * FLAG-1's one control, both directions. `void`, never awaited — nothing on the capture
   * critical path may block on the recorder; the band re-renders from the subscription when
   * the recorder says so.
   */
  const onFlagPress = useCallback((): void => {
    setNotice(null);
    void recorder.pressFlag(match).then(
      (action) => {
        setNotice(flagNoticeFor(action));
      },
      (cause: unknown) => {
        const message = cause instanceof Error ? cause.message : String(cause);
        setNotice(message);
        log.error(MODULE, 'the flag control failed', { message });
      },
    );
  }, [match]);

  return (
    <footer className="capb-bottom" data-testid="capb-bottomband" data-recording={live ? 'true' : 'false'}>
      {/* The message overlay: transient, NON-INTERACTIVE sentences off the band's top edge —
          no part of any band's box. `pointer-events: none` on the container is load-bearing:
          an overlay above a live capture surface that swallowed a tap would cost the user an
          increment (the hint's dismiss alone opts back in). The pill is NOT here — see the
          header: interactive chrome gets reserved space, never an overlay. */}
      <div className="bandb-overlay">
        {view.hint !== null && (
          <p className="bandb-hint" role="status" data-testid="bandb-hint">
            {view.hint}
            <button
              type="button"
              className="bandb-hint__dismiss"
              onClick={() => {
                recorder.dismissHint();
              }}
            >
              OK
            </button>
          </p>
        )}

        {notice !== null && (
          <p className="bandb-notice" role="status" data-testid="bandb-notice">
            {notice}
          </p>
        )}

      </div>

      {/* The reserved pill row — fixed height, occupied or not (scenario 9's "directly above
          it" as chrome, so the pill can never cover a row or a tile). */}
      <div className="bandb-pillrow" data-testid="bandb-pillrow">
        <UndoPillB match={match} rows={undoRows} onUndone={onUndone} onNotice={setNotice} />
      </div>

      <div className="bandb-controls">
        {/* B-12 — the set-piece button, in the slot B-11 reserved at the same 44 pt geometry
            (the flag never re-centered). It renders in every state, like every control on this
            band (a control that materialises on a predicate is a reflow on committed chrome);
            pre-kickoff the SHEET says why nothing records. */}
        <button
          type="button"
          className="bandb-flank"
          data-slot="setpiece"
          data-testid="bandb-setpiece"
          aria-label="Set piece"
          onClick={onSetPiece}
        >
          <Icon name="set-piece" />
        </button>

        <button
          type="button"
          className="bandb-flag"
          data-testid="bandb-flag"
          aria-pressed={live}
          onClick={onFlagPress}
          style={{ minHeight: `${String(config.MIN_TOUCH_TARGET_PT)}px` }}
        >
          {live ? (
            <>
              <span className="bandb-flag__dot" aria-hidden="true" />
              <span className="bandb-flag__label">
                Recording — {recordingCaption(recording.subject.kind)} at{' '}
                {String(recording.matchMinute)}′ · tap to stop
              </span>
            </>
          ) : (
            <>
              <Icon name="flag" />
              <span className="bandb-flag__label">Flag</span>
            </>
          )}
        </button>

        {/* B-13 — the flags-review button, in the slot B-11 reserved at the same 44 pt geometry
            (the flag never re-centered). It renders in every state, like every control on this
            band; pre-kickoff the SURFACE says why nothing is there (its honest empty state). */}
        <button
          type="button"
          className="bandb-flank"
          data-slot="flagsreview"
          data-testid="bandb-flagsreview"
          aria-label="Flags review"
          onClick={onFlagsReview}
        >
          <Icon name="report" />
        </button>

        <button
          type="button"
          className="bandb-more"
          data-testid="bandb-more"
          aria-label="More"
          onClick={() => {
            setOverflowOpen(true);
          }}
        >
          ⋯
        </button>
      </div>

      {overflowOpen && (
        <OverflowSheetB
          match={match}
          onClose={() => {
            setOverflowOpen(false);
          }}
          onTransitioned={onTransitioned}
        />
      )}
    </footer>
  );
}
